"use client";

import { cn } from "@/lib/utils";
import type { SearchFilterType } from "./column-search-input";

// =============================================================================
// Types
// =============================================================================

export interface ActiveFilter {
  columnKey: string;
  columnLabel: string;
  displayValue: string;
  filterType: SearchFilterType;
  /** Individual chip values for `tags` filters; one entry for other types when set. */
  values?: string[];
  apiParams: Record<string, string | string[]>;
}

export interface ActiveFiltersBarProps {
  filters: ActiveFilter[];
  onRemoveFilter: (columnKey: string, value?: string) => void;
  onClearAll: () => void;
  clearAllLabel?: string;
  activeFiltersLabel?: string;
  className?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function getFilterChipEntries(
  filter: ActiveFilter,
  onRemoveFilter: (columnKey: string, value?: string) => void
): { key: string; label: string; onRemove: () => void }[] {
  if (filter.filterType === "tags") {
    const values =
      filter.values ??
      (() => {
        const apiVal = Object.values(filter.apiParams)[0];
        if (Array.isArray(apiVal)) return apiVal;
        if (typeof apiVal === "string") {
          return apiVal
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);
        }
        return filter.displayValue
          ? [filter.displayValue]
          : [];
      })();

    return values.map((value) => ({
      key: `${filter.columnKey}:${value}`,
      label: `${filter.columnLabel}: ${value}`,
      onRemove: () => onRemoveFilter(filter.columnKey, value),
    }));
  }

  return [
    {
      key: filter.columnKey,
      label: `${filter.columnLabel}: ${filter.displayValue}`,
      onRemove: () => onRemoveFilter(filter.columnKey),
    },
  ];
}

// =============================================================================
// Active Filters Bar
// =============================================================================

function ActiveFiltersBar({
  filters,
  onRemoveFilter,
  onClearAll,
  clearAllLabel = "Clear all filters",
  activeFiltersLabel = "Active filters",
  className,
}: ActiveFiltersBarProps) {
  if (filters.length === 0) return null;

  const chips = filters.flatMap((filter) =>
    getFilterChipEntries(filter, onRemoveFilter)
  );

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 overflow-hidden",
        className
      )}
    >
      {/* Left: label + count */}
      <div className="flex items-center gap-2 shrink-0">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-base font-semibold text-text-primary">
            {activeFiltersLabel}
          </span>
          <span className="inline-flex items-center justify-center min-w-[21px] h-[21px] px-[7px] rounded-sm bg-brand-700 text-white text-[10.5px] font-bold">
            {chips.length}
          </span>
        </div>
      </div>

      {/* Middle: filter chips */}
      <div className="flex items-center gap-2 ml-4 overflow-x-auto">
        {chips.map((chip) => (
          <FilterChip
            key={chip.key}
            label={chip.label}
            onRemove={chip.onRemove}
          />
        ))}
      </div>

      {/* Right: clear all button */}
      <div className="ml-auto shrink-0">
        <button
          type="button"
          onClick={onClearAll}
          className="flex items-center gap-[7px] px-[10.5px] py-[7px] text-sm font-medium text-brand-600 rounded-sm transition-colors"
        >
          <i className="pi pi-filter-slash text-sm" />
          <span className="hover:underline">{clearAllLabel}</span>
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Filter Chip
// =============================================================================

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-[7px] px-[7px] py-0.5 bg-slate-100 border border-border-secondary rounded-sm text-sm text-text-primary whitespace-nowrap">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 text-text-tertiary hover:text-text-primary transition-colors"
        aria-label={`Remove filter: ${label}`}
      >
        <i className="pi pi-times-circle text-sm" />
      </button>
    </span>
  );
}

ActiveFiltersBar.displayName = "ActiveFiltersBar";

export { ActiveFiltersBar };
